import React, { useMemo, useState } from 'react';

const formatLabel = (val) => val.replace(/_/g, ' ');

const emptyForm = {
  genus: '',
  species: '',
  common_name: '',
  status: 'healthy',
  form: 'standard',
  height_m: '',
  canopy_spread_m: '',
  dbh_cm: '',
  number_of_stems: 'single',
  zone: '',
  occupation: '',
};

const NewTreeForm = ({
  job,
  coords,
  enums = {},
  species = [],
  lastDraft,
  onSave,
  onIgnore,
}) => {
  const initial = useMemo(() => ({
    ...emptyForm,
    genus: lastDraft?.genus || '',
    species: lastDraft?.species || '',
    common_name: lastDraft?.common_name || '',
    form: lastDraft?.form || 'standard',
    number_of_stems: lastDraft?.number_of_stems || 'single',
  }), [lastDraft]);

  const [form, setForm] = useState(initial);

  const statuses = enums.tree_status || ['healthy', 'monitor', 'priority', 'dead', 'removed', 'stump'];
  const forms = enums.tree_form || ['standard', 'multi_stem', 'pollard', 'coppice', 'weeping', 'columnar', 'spreading', 'other'];
  const stems = enums.stem_count || ['single', 'double', 'triple', 'multi_3_5', 'multi_6_plus'];
  const riskLevels = enums.risk_level || ['high', 'mid', 'low'];

  const buildPayload = (statusOverride) => ({
    ...form,
    status: statusOverride || form.status,
    job_id: job.job_id,
    gps_lat: coords.lat,
    gps_lng: coords.lng,
    tree_type: 'individual_tree',
    height_m: form.height_m === '' ? null : parseFloat(form.height_m),
    canopy_spread_m: form.canopy_spread_m === '' ? null : parseFloat(form.canopy_spread_m),
    dbh_cm: form.dbh_cm === '' ? null : parseFloat(form.dbh_cm),
    zone: form.zone || null,
    occupation: form.occupation || null,
    structural_defects: [],
    physiological_defects: [],
  });

  const handleSpeciesPick = (e) => {
    const entry = species.find((s) => s.common_name === e.target.value);
    if (entry) {
      setForm({
        ...form,
        genus: entry.genus,
        species: entry.species,
        common_name: entry.common_name,
      });
    }
  };

  const handleSubmit = (e, statusOverride) => {
    e.preventDefault();
    onSave?.(buildPayload(statusOverride));
  };

  return (
    <form className="new-tree-form" onSubmit={(e) => handleSubmit(e)}>
      <div className="panel-section">
        <h2>New tree</h2>
        <p className="empty-inline">{job.name}</p>
        <p className="coords-hint">{coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</p>
      </div>

      <div className="form-group">
        <label>Species</label>
        <select value={form.common_name} onChange={handleSpeciesPick}>
          <option value="">Select species…</option>
          {species.map((s) => (
            <option key={`${s.genus}-${s.species}`} value={s.common_name}>{s.common_name}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Common name</label>
        <input value={form.common_name} onChange={(e) => setForm({ ...form, common_name: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Genus</label>
        <input value={form.genus} onChange={(e) => setForm({ ...form, genus: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Species (latin)</label>
        <input value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Status</label>
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          {statuses.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Form</label>
        <select value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })}>
          {forms.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Stems</label>
        <select value={form.number_of_stems} onChange={(e) => setForm({ ...form, number_of_stems: e.target.value })}>
          {stems.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Height (m)</label>
        <input type="number" step="0.1" value={form.height_m} onChange={(e) => setForm({ ...form, height_m: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Canopy spread (m)</label>
        <input type="number" step="0.1" value={form.canopy_spread_m} onChange={(e) => setForm({ ...form, canopy_spread_m: e.target.value })} />
      </div>
      <div className="form-group">
        <label>DBH (cm)</label>
        <input type="number" step="0.1" value={form.dbh_cm} onChange={(e) => setForm({ ...form, dbh_cm: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Zone</label>
        <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}>
          <option value="">—</option>
          {riskLevels.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Occupation</label>
        <select value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })}>
          <option value="">—</option>
          {riskLevels.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
        </select>
      </div>

      <div className="form-actions-sticky">
        <button type="submit" className="btn btn-primary btn-touch">Save</button>
        <button
          type="button"
          className="btn btn-secondary btn-touch"
          style={{ color: '#333', background: '#eee' }}
          onClick={(e) => handleSubmit(e, 'removed')}
        >
          Removed
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-touch"
          style={{ color: '#333', background: '#eee' }}
          onClick={onIgnore}
        >
          Ignore
        </button>
      </div>
    </form>
  );
};

export default NewTreeForm;
